import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { config } from '../../components/Init/Init'
import { tokenizer, separateLobbyForIOS, cleanUpEntryTags, extractLoyaltyTierParts } from '../../services/utils'
import { useGet } from '../apiHooks'
import { usePlayerStore, useCasinoStore } from '../../services/store'
import { useSetPlayerData, useSetPlayerLoyaltyData } from '../../services/store/playerStore/playerActions'
import { isNullOrUndefined } from '../../services/helpers'
import { useConfigValueByCurrentDevice } from '../useConfigValueByCurrentDevice'

export const useGetUserProfileData = requestData => {
  const playerStore = usePlayerStore()
  const playerUserId = playerStore.use.userId()

  const { GET } = useGet()

  const hasRequestData = requestData && Array.isArray(requestData) && requestData.length !== 0

  const playerProfileDataUrl =
    config.apiUrl +
    tokenizer(config.getProfileUrl, {
      '{appId}': config.applicationId,
      '{userId}': playerUserId,
      '{families}': hasRequestData ? requestData.join(',') : config.playerProfileProperties.clientControls
    })

  const getProfileData = () => {
    return GET(playerProfileDataUrl)
      .then(data => data)
      .catch(e => {
        console.log('Failed to get profile data.', e)
      })
  }

  return { getProfileData }
}

export const useUpdateUserProfileData = () => {
  const playerStore = usePlayerStore()
  const playerUserId = playerStore.use.userId()
  const playerTagsBanner = playerStore.use.tagsBanner()
  const playerTagsInbox = playerStore.use.tagsInbox()
  const playerTagsGames = playerStore.use.tagsGames()
  const playerTagsPlayer = playerStore.use.tagsPlayer()
  const playerPurchaseLimitKycStatus = playerStore.use.purchaseLimitKycStatus()
  const playerLoyaltyData = playerStore.use.playerLoyaltyData()

  const casinoStore = useCasinoStore()
  const casinoSweepstakesOrigin = casinoStore.use.casinoSweepstakesOrigin()
  const enableFirebaseLoyaltyUserData = useConfigValueByCurrentDevice(config.enableFirebaseLoyaltyUserData)

  const { getProfileData } = useGetUserProfileData([
    config.playerProfileProperties.clientControls,
    config.playerProfileProperties.sweepsUserProfile,
    config.playerProfileProperties.dfRunningStats
  ])
  const location = useLocation()
  const { setPlayerData } = useSetPlayerData()
  const { setPlayerLoyaltyData } = useSetPlayerLoyaltyData()

  const [playerCustomLobbyId, setPlayerCustomLobbyId] = useState(null)
  const [playerProfileDataReady, setPlayerProfileDataReady] = useState(false)

  const updateCurrentTierData = (tier, progress) => {
    if (!enableFirebaseLoyaltyUserData) {
      setPlayerLoyaltyData({
        currentTier: tier ? extractLoyaltyTierParts(tier) : {},
        progress: progress
      })
    }
  }

  const updateDataFromPlayerProfile = useCallback(() => {
    if (playerUserId) {
      // set these prior to load, so they're assigned asap
      if (isNullOrUndefined(playerLoyaltyData.currentTier.fullTier) || isNullOrUndefined(playerLoyaltyData.progress)) {
        updateCurrentTierData('1', 0)
      }

      getProfileData().then(res => {
        if (res && res.data) {
          if (res.data.client_controls) {
            const { custom_lobby_id, tagsBanner, tagsInbox, tagsGames, tagsPlayer } = res.data.client_controls

            if (custom_lobby_id) {
              setPlayerCustomLobbyId(custom_lobby_id)
            }

            if (tagsBanner !== playerTagsBanner) {
              setPlayerData({
                tagsBanner: tagsBanner
              })
            }

            if (tagsInbox !== playerTagsInbox) {
              setPlayerData({
                tagsInbox: tagsInbox
              })
            }

            if (tagsPlayer !== playerTagsPlayer) {
              setPlayerData({
                tagsPlayer: tagsPlayer
              })
            }

            //tagsGames will be a string of format: ['sale', 'special-feature', 'referral']
            let modifiedTagsGames = cleanUpEntryTags(tagsGames)
            if (config.forceAddExtraGameTags) {
              // Hack to not show Evolution games for iOS webview. Add 'evo_allowed' to tagsGames for iOS webview.
              if (!separateLobbyForIOS(casinoSweepstakesOrigin)) {
                if (!tagsGames || tagsGames.length === 2) {
                  modifiedTagsGames = `['evo_allowed']`
                } else {
                  modifiedTagsGames = tagsGames.substring(0, tagsGames.length - 1) + `, 'evo_allowed']`
                }
              }
            }

            if (modifiedTagsGames !== playerTagsGames) {
              setPlayerData({
                tagsGames: modifiedTagsGames
              })
            }
          }

          if (res.data.sweeps_user_profile && res.data.sweeps_user_profile.loyalty_tier) {
            updateCurrentTierData(
              res.data.sweeps_user_profile.loyalty_tier,
              res.data.sweeps_user_profile.hasOwnProperty('loyalty_tier_progress')
                ? res.data.sweeps_user_profile.loyalty_tier_progress * 100
                : 0
            )
          }

          if (
            res.data.df_running_stats &&
            res.data.df_running_stats.kyc_status &&
            res.data.df_running_stats.kyc_status !== playerPurchaseLimitKycStatus
          ) {
            setPlayerData({
              newValues: {
                purchaseLimitKycStatus: res.data.df_running_stats.kyc_status
              }
            })
          }
        }

        setPlayerProfileDataReady(true)
      })
    } else {
      setPlayerProfileDataReady(true)
    }
  }, [playerUserId, playerLoyaltyData.currentTier.fullTier, playerLoyaltyData.progress])

  useEffect(() => {
    // automatically update data from the player profile on login or location change
    updateDataFromPlayerProfile()
  }, [playerUserId, location])

  return { playerCustomLobbyId, playerProfileDataReady }
}
